import { useState, useEffect, useRef } from 'react'
import { motion } from 'motion/react'
import { Input } from '@/components/ui/Input'
import { Button } from '@/components/ui/Button'
import { ArrowRight, ArrowLeft } from 'lucide-react'
import type { FormData } from '@/hooks/useSubmissionWizard'

// Rotating placeholder titles - creative photography title examples
const PLACEHOLDER_TITLES = [
  'Golden Hour',
  'Morning Light',
  'Shadows & Dreams',
  'The Quiet Moment',
  'Reflections',
  'Urban Geometry',
  "Nature's Canvas",
  'Fleeting Beauty',
  'Whispers of Wind',
  'Timeless',
  'Between the Lines',
  'Solitude',
  'Chasing Light',
  'The View From Here',
  'Unspoken',
  'First Light',
  'After the Rain',
  'Stillness',
  'Edge of Tomorrow',
  'In Bloom',
  'Horizons',
  'The Long Way Home',
  'Patterns',
  'Wanderlust',
  'Serenity',
  'Untitled Dream',
  'Fragments',
  'Beyond the Frame',
]

interface TitleStepProps {
  formData: FormData
  updateField: <K extends keyof FormData>(field: K, value: FormData[K]) => void
  onNext: () => void
  onBack: () => void
}

export function TitleStep({ formData, updateField, onNext, onBack }: TitleStepProps) {
  const inputRef = useRef<HTMLInputElement>(null)
  const [error, setError] = useState<string | null>(null)
  const [placeholderIndex, setPlaceholderIndex] = useState(0)

  // Focus input on mount
  useEffect(() => {
    const timer = setTimeout(() => inputRef.current?.focus(), 100)
    return () => clearTimeout(timer)
  }, [])

  // Rotate placeholder every 3 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      setPlaceholderIndex((prev) => (prev + 1) % PLACEHOLDER_TITLES.length)
    }, 3000)
    return () => clearInterval(interval)
  }, [])

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (!formData.photoTitle.trim()) {
      setError('Please give your photo a title')
      return
    }
    onNext()
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && formData.photoTitle.trim()) {
      e.preventDefault()
      onNext()
    }
  }

  return (
    <form onSubmit={handleSubmit} className="mx-auto max-w-xl space-y-8">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="space-y-3">
        <h2 className="text-foreground font-display text-3xl font-bold md:text-4xl lg:text-5xl">
          Give your photo a title
        </h2>
        <p className="text-foreground-muted text-lg">What do you call this moment?</p>
      </motion.div>

      {/* Photo thumbnail preview */}
      {formData.photoPreview && (
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="mx-auto h-32 w-32 overflow-hidden rounded-xl shadow-lg"
        >
          <img src={formData.photoPreview} alt="Your submission" className="h-full w-full object-cover" />
        </motion.div>
      )}

      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>
        <Input
          ref={inputRef}
          type="text"
          placeholder={PLACEHOLDER_TITLES[placeholderIndex]}
          value={formData.photoTitle}
          onChange={(e) => {
            setError(null)
            updateField('photoTitle', e.target.value)
          }}
          onKeyDown={handleKeyDown}
          error={error ?? undefined}
          className="text-center text-xl"
        />
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.2 }}
        className="flex items-center gap-4"
      >
        <Button type="button" variant="ghost" size="lg" onClick={onBack}>
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <Button
          type="submit"
          size="lg"
          disabled={!formData.photoTitle.trim()}
          className="flex-1 md:min-w-[200px] md:flex-none"
        >
          Continue
          <ArrowRight className="h-5 w-5" />
        </Button>
        <span className="text-foreground-subtle hidden text-sm md:block">or press Enter</span>
      </motion.div>
    </form>
  )
}
